/* Program Name: LookUpByStoreNum.js
 **  Description: panel for the user look up modal to veiw users based on store num
 **
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2022-05-14  STT  Initial program
 *********************************************************************************/

/**React imports */
import React, { useState, useEffect, useRef } from 'react'

/**ezPOP imports */
import { PrimaryButton, SquarePrimaryButton } from '../ActionColorButtons'
import BasicGrid from '../BasicGrid'
import { FlexBox, FlexOne } from '../CustomDivs'
import { FormTextField } from '../CustomInputs'

/**MUI imports */
import { Checkbox, FormControlLabel, Tooltip } from '@mui/material'
import { PersonSearch } from '@mui/icons-material'

/**API imports */
import { getStoreLookup } from '../../../API/generalAPI'

/**Columns imports */
import { columns } from './userLookUpColumns'

/**Context imports */
import { useLoading } from '../../../Context/LoadingContext'

/******************************************************************************/
/**LookUpByStoreNum :
 *  displays the users based that are linked to the searched store num
 * @param {Function} getUserDetail - function to get the details of a single
 *  user
 */
function LookUpByStoreNum({ getUserDetail }) {
  /******************************************************************************/
  /**State Variables*/

  //Context end point to access the global loading modal
  const loading = useRef(useLoading())
  //Boolean that determines if the list of users includes users from all domains
  //or just the current customers domain
  const [allDomain, setAllDomain] = useState(false)
  //Boolean that determines if the list of users includes only proxiable targets
  //or also ones that cannot
  const [proxyTar, setProxyTar] = useState(true)
  //Contains the store number that the list of users will be filtered on
  const [searchNumber, setSearchNumber] = useState('')
  //List of users being displayed
  const [filteredUsers, setFilteredUsers] = useState([])
  //List of options to send when fetching the list of users
  const [options, setOptions] = useState('DefaultDomain,ProxyTargets')

  /** Handle Domain Checked :
   *
   */
  const handleDomainChecked = (e) => {
    setAllDomain(e.target.checked)
    if (e.target.checked) {
      setOptions(`${proxyTar ? 'ProxyTargets' : ''}`)
    } else {
      setOptions(`DefaultDomain,${proxyTar ? 'ProxyTargets' : ''}`)
    }
  }

  const handleProxyTarChecked = (e) => {
    setProxyTar(e.target.checked)
    if (e.target.checked) {
      setOptions(`ProxyTargets,${allDomain ? '' : 'DefaultDomain'}`)
    } else {
      setOptions(`${allDomain ? '' : 'DefaultDomain'}`)
    }
  }

  const handleStoreNumSearch = (input) => (e) => {
    setSearchNumber(e.target.value)
  }

  const handleStoreSearch = () => {
    loading.current.startLoading()
    getStoreLookup(searchNumber, options)
      .then((response) => {
        if (!response.data.meta[0].err) {
          if (response.data.ttUser) {
            setFilteredUsers(response.data.ttUser)
          }
        } else {
          setFilteredUsers([])
        }
      })
      .catch((error) => {
        alert.current.errorMessage(error.message)
      })
      .then(() => {
        loading.current.stopLoading()
      })
  }

  //use effect handles any keystrokes that are hit while this component is loaded
  useEffect(() => {
    //function taking in the keystroke event
    const handleKeyStroke = (e) => {
      //triggering different functions based on keyCode of the key stroke
      switch (e.keyCode) {
        case 13: {
          handleStoreSearch()
        }

        default: {
          break
        }
      }
    }

    window.addEventListener('keydown', handleKeyStroke)

    //removes the event listener on the function recall
    return () => {
      window.removeEventListener('keydown', handleKeyStroke)
    }
  })

  /** Grid Columns */
  // sets any nessacary action buttons to the columns for the grid
  const gridColumns = [
    {
      headerName: 'Actions',
      field: ' ',
      width: 120,
      resizable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <FlexBox>
            <Tooltip disableInteractive title={'Click to View Details'}>
              <SquarePrimaryButton
                onClick={() => {
                  getUserDetail(params.row.id)
                }}
              >
                <PersonSearch />
              </SquarePrimaryButton>
            </Tooltip>
          </FlexBox>
        )
      },
    },
    ...columns,
  ]

  return (
    <div>
      <FlexBox>
        <FormTextField
          onKeyDown={(e) => {
            if (e.keyCode === 13) {
              handleStoreSearch()
            }
          }}
          inputRef={(input) => input && input.focus()}
          value={searchNumber}
          label={'Store Number'}
          handleText={handleStoreNumSearch}
          inputErrors={{}}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={allDomain}
              onChange={handleDomainChecked}
              name="rememberMe"
            />
          }
          label={<p>Show Serigraph Users</p>}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={proxyTar}
              onChange={handleProxyTarChecked}
              name="rememberMe"
            />
          }
          label={<p>Show Proxyable Targets</p>}
        />
        <FlexOne />
        <div style={{ margin: '0px 8px 0px 8px' }}>
          <PrimaryButton onClick={handleStoreSearch}>Search</PrimaryButton>
        </div>
      </FlexBox>
      <BasicGrid
        gridTitle={''}
        gridData={filteredUsers}
        realColumns={gridColumns}
        rowStyles={{ height: 600 }}
      />
    </div>
  )
}

export default LookUpByStoreNum
